import { useEffect, useMemo } from "react";

import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { z } from "zod";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { SearchableSelect } from "~/components/ui/searchable-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Switch } from "~/components/ui/switch";
import { useAccounts } from "~/hooks/contabilidad/useAccounts";
import { useCompanies } from "~/hooks/contabilidad/useCompanies";

import type {
  SupplierCompanyProfile,
  SupplierCompanyProfileFormData,
} from "~/types/purchasing";

const formSchema = z.object({
  company_id: z.string().min(1, "Seleccione una empresa"),
  ap_account_code: z.string().optional().nullable(),
  default_expense_account_id: z.string().optional().nullable(),
  payment_condition: z.string().optional().nullable(),
  is_active: z.boolean(),
});

type FormValues = z.infer<typeof formSchema>;

interface SupplierCompanyProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  supplierId: string;
  profile?: SupplierCompanyProfile;
  // Company ids that already have a profile — excluded from the picker when creating.
  takenCompanyIds?: string[];
  onSubmit: (data: SupplierCompanyProfileFormData) => void;
  isLoading?: boolean;
}

export function SupplierCompanyProfileDialog({
  open,
  onOpenChange,
  supplierId,
  profile,
  takenCompanyIds = [],
  onSubmit,
  isLoading = false,
}: SupplierCompanyProfileDialogProps) {
  const isEditing = !!profile;
  const { data: companies = [] } = useCompanies();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      company_id: profile?.company_id ?? "",
      ap_account_code: profile?.ap_account_code ?? "2-1-03-001",
      default_expense_account_id: profile?.default_expense_account_id ?? null,
      payment_condition: profile?.payment_condition ?? "",
      is_active: profile?.is_active ?? true,
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        company_id: profile?.company_id ?? "",
        ap_account_code: profile?.ap_account_code ?? "2-1-03-001",
        default_expense_account_id: profile?.default_expense_account_id ?? null,
        payment_condition: profile?.payment_condition ?? "",
        is_active: profile?.is_active ?? true,
      });
    }
  }, [open, profile, form]);

  const companyId = form.watch("company_id");
  const { data: accounts = [] } = useAccounts({
    company_id: companyId || undefined,
    is_active: true,
  });

  const companyOptions = useMemo(
    () =>
      companies.filter(
        (c) =>
          c.id === profile?.company_id || !takenCompanyIds.includes(c.id)
      ),
    [companies, takenCompanyIds, profile?.company_id]
  );

  const accountOptions = useMemo(
    () =>
      accounts
        .filter(
          (a) => a.account_type === "gasto" || a.account_type === "activo"
        )
        .slice()
        .sort((a, b) => a.code.localeCompare(b.code))
        .map((a) => ({
          value: a.id,
          label: `${a.code} · ${a.name}`,
          searchTerms: `${a.code} ${a.name}`,
        })),
    [accounts]
  );

  const handleSubmit = (data: FormValues) =>
    onSubmit({
      supplier_id: supplierId,
      company_id: data.company_id,
      ap_account_code: data.ap_account_code?.trim() || null,
      default_expense_account_id: data.default_expense_account_id || null,
      payment_condition: data.payment_condition?.trim() || null,
      is_active: data.is_active,
    });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Editar cuentas por empresa" : "Asignar empresa"}
          </DialogTitle>
          <DialogDescription>
            Configure las cuentas contables de este proveedor para una empresa.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="company_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Empresa *</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                    disabled={isEditing}
                  >
                    <FormControl>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Seleccione una empresa" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {companyOptions.map((company) => (
                        <SelectItem key={company.id} value={company.id}>
                          {company.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="ap_account_code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cuenta por pagar (código)</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="2-1-03-001"
                      className="h-9"
                      {...field}
                      value={field.value ?? ""}
                    />
                  </FormControl>
                  <FormDescription>
                    Código de la cuenta de proveedores en el plan de la empresa.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="default_expense_account_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cuenta de gasto por defecto</FormLabel>
                  <FormControl>
                    <SearchableSelect
                      value={field.value ?? ""}
                      onValueChange={field.onChange}
                      options={accountOptions}
                      placeholder="Seleccionar cuenta…"
                      emptyMessage={
                        companyId ? "Sin cuentas" : "Elija una empresa"
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="payment_condition"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Condición de pago</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Ej: CREDITO 30 DIAS"
                      className="h-9"
                      {...field}
                      value={field.value ?? ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>Activo</FormLabel>
                    <FormDescription>
                      Habilita este proveedor para la empresa.
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="flex justify-end pt-2">
              <Button type="submit" disabled={isLoading}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isEditing ? "Guardar" : "Asignar"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
